/**
 * The T-chart: two columns under two headings, for pros and cons, facts and opinions,
 * cause and effect, or any other pair. Entries are optional: an empty chart prints with
 * blank rows for students to fill in.
 */

export const MIN_ROWS = 4;
export const MAX_ROWS = 16;
export const DEFAULT_ROWS = 10;
export const MAX_HEADING = 40;

const LINE_BREAK = /\r?\n/;

export const PRESETS = [
  "prosCons",
  "factsOpinions",
  "advantagesDisadvantages",
  "causeEffect",
  "problemSolution",
  "beforeAfter",
  "forAgainst",
] as const;
export type Preset = (typeof PRESETS)[number];

/** The non-blank lines of a column, tidied. */
export function entryLines(text: string): string[] {
  return text
    .split(LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

export function clampRows(value: number): number {
  if (!Number.isFinite(value)) {
    return DEFAULT_ROWS;
  }
  return Math.min(MAX_ROWS, Math.max(MIN_ROWS, Math.round(value)));
}

export interface ChartRows {
  left: string[];
  right: string[];
  /** Rows on the sheet: the chosen number, or more if the entries need them (up to the maximum). */
  count: number;
  /** Entries that did not fit. */
  cut: number;
}

/** Lay the two columns out on `rows` rows, growing to fit the longer column. */
export function layoutRows(
  leftText: string,
  rightText: string,
  rows: number
): ChartRows {
  const left = entryLines(leftText);
  const right = entryLines(rightText);
  const longest = Math.max(left.length, right.length);
  const count = Math.min(MAX_ROWS, Math.max(clampRows(rows), longest));
  return {
    left: left.slice(0, count),
    right: right.slice(0, count),
    count,
    cut: Math.max(0, left.length - count) + Math.max(0, right.length - count),
  };
}

/** A heading as typed, trimmed and limited in length. */
export function cleanHeading(text: string): string {
  return text.trim().slice(0, MAX_HEADING);
}
